/**
 * 操作审计日志（旧 views/system/AuditLogsView.vue）：统计卡片、可折叠的筛选面板、分页列表、详情弹窗。
 * 筛选条件点「查询」后生效；翻页沿用已生效的条件。
 */
import { useState } from 'react'
import {
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  InputNumber,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
  type TableColumnsType
} from 'antd'
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  FileTextOutlined,
  FilterOutlined,
  ReloadOutlined,
  SearchOutlined,
  UserOutlined
} from '@ant-design/icons'
import dayjs, { type Dayjs } from 'dayjs'
import type { AuditLogParams } from '@/api/services/admin'
import { PageHeader } from '@/components/PageHeader'
import { actionColor, actionLabel, formatAuditTime } from './constants'
import { useAuditActionOptions, useAuditLogs, useAuditTargetTypeOptions, type AuditLog } from './hooks'
import { AuditLogDetailModal } from './AuditLogDetailModal'
import styles from './AuditLogsPage.module.css'

interface AuditFilters {
  admin_id?: number
  action?: string
  target_type?: string
  success?: boolean
  dateRange?: [Dayjs, Dayjs] | null
}

const SUCCESS_OPTIONS = [
  { value: true, label: '成功' },
  { value: false, label: '失败' }
]

function datePresets(): Array<{ label: string; value: [Dayjs, Dayjs] }> {
  return [
    { label: '最近1小时', value: [dayjs().subtract(1, 'hour'), dayjs()] },
    { label: '最近24小时', value: [dayjs().subtract(24, 'hour'), dayjs()] },
    { label: '最近7天', value: [dayjs().subtract(7, 'day'), dayjs()] },
    { label: '最近30天', value: [dayjs().subtract(30, 'day'), dayjs()] }
  ]
}

// 筛选条件转成接口参数：日期范围用 ISO 时间；未选的条件不传
export function toAuditParams(filters: AuditFilters, page: number, pageSize: number): AuditLogParams {
  const { dateRange, ...rest } = filters
  const params: AuditLogParams = { page, page_size: pageSize }
  for (const [key, value] of Object.entries(rest)) {
    if (value !== undefined && value !== null) Object.assign(params, { [key]: value })
  }
  if (dateRange) {
    params.start_date = dateRange[0].toISOString()
    params.end_date = dateRange[1].toISOString()
  }
  return params
}

export function AuditLogsPage() {
  const [form] = Form.useForm<AuditFilters>()
  const [showFilters, setShowFilters] = useState(false)
  const [applied, setApplied] = useState<AuditFilters>({})
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null)
  const { data, isFetching, refetch } = useAuditLogs(toAuditParams(applied, page, pageSize))
  const actionOptions = useAuditActionOptions()
  const targetTypeOptions = useAuditTargetTypeOptions()
  const stats = data?.stats

  const search = () => {
    setApplied(form.getFieldsValue())
    setPage(1)
  }

  const reset = () => {
    form.resetFields()
    setApplied({})
    setPage(1)
  }

  const columns: TableColumnsType<AuditLog> = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 80 },
    {
      title: '管理员',
      key: 'admin',
      width: 150,
      render: (_, record) => (
        <div>
          <div className={styles.strong}>{record.admin_name || '系统'}</div>
          <Typography.Text type="secondary" className={styles.small}>
            {record.admin_username}
          </Typography.Text>
        </div>
      )
    },
    {
      title: '操作类型',
      key: 'action',
      width: 180,
      render: (_, record) => <Tag color={actionColor(record.action)}>{actionLabel(record.action)}</Tag>
    },
    {
      title: '目标',
      key: 'target',
      width: 150,
      render: (_, record) =>
        record.target_type ? (
          <div>
            <div className={styles.strong}>{record.target_type}</div>
            {record.target_id && (
              <Typography.Text type="secondary" className={styles.small}>
                ID: {record.target_id}
              </Typography.Text>
            )}
          </div>
        ) : (
          <Typography.Text type="secondary">-</Typography.Text>
        )
    },
    {
      title: '状态',
      key: 'status',
      width: 80,
      render: (_, record) => (
        <Tag color={record.success ? 'success' : 'error'}>{record.success ? '成功' : '失败'}</Tag>
      )
    },
    { title: 'IP地址', dataIndex: 'ip_address', key: 'ip_address', width: 140 },
    {
      title: '操作时间',
      key: 'created_at',
      width: 180,
      render: (_, record) => formatAuditTime(record.created_at)
    },
    {
      title: '操作',
      key: 'actions',
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <Button type="link" size="small" onClick={() => setSelectedLog(record)}>
          查看详情
        </Button>
      )
    }
  ]

  return (
    <div>
      <PageHeader
        title="操作审计日志"
        subtitle="记录所有管理员的操作行为"
        extra={
          <Space>
            <Button
              type={showFilters ? 'primary' : 'default'}
              icon={<FilterOutlined />}
              onClick={() => setShowFilters((current) => !current)}
            >
              筛选{showFilters ? '（已展开）' : ''}
            </Button>
            <Button type="primary" icon={<ReloadOutlined />} loading={isFetching} onClick={() => refetch()}>
              刷新
            </Button>
          </Space>
        }
      />

      <Row gutter={[16, 16]} className={styles.section}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic title="总操作记录" value={stats?.total_logs ?? 0} prefix={<FileTextOutlined />} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="成功操作"
              value={stats?.success_count ?? 0}
              prefix={<CheckCircleOutlined />}
              styles={{ content: { color: 'var(--ant-color-success)' } }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="失败操作"
              value={stats?.failed_count ?? 0}
              prefix={<CloseCircleOutlined />}
              styles={{ content: { color: 'var(--ant-color-error)' } }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic title="活跃管理员" value={stats?.admin_count ?? 0} prefix={<UserOutlined />} />
          </Card>
        </Col>
      </Row>

      {/* 收起时保留已填的条件 */}
      <Card className={styles.filters} hidden={!showFilters}>
        <Form form={form} layout="inline">
          <Form.Item label="管理员ID" name="admin_id">
            <InputNumber placeholder="输入管理员ID" min={1} className={styles.adminIdInput} />
          </Form.Item>
          <Form.Item label="操作类型" name="action">
            <Select
              aria-label="操作类型"
              placeholder="选择操作类型"
              allowClear
              className={styles.actionSelect}
              loading={actionOptions.isLoading}
              options={(actionOptions.data ?? []).map((action) => ({ value: action, label: actionLabel(action) }))}
            />
          </Form.Item>
          <Form.Item label="目标类型" name="target_type">
            <Select
              aria-label="目标类型"
              placeholder="选择目标类型"
              allowClear
              className={styles.targetSelect}
              loading={targetTypeOptions.isLoading}
              options={(targetTypeOptions.data ?? []).map((type) => ({ value: type, label: type }))}
            />
          </Form.Item>
          <Form.Item label="状态" name="success">
            <Select
              aria-label="状态"
              placeholder="选择状态"
              allowClear
              className={styles.statusSelect}
              options={SUCCESS_OPTIONS}
            />
          </Form.Item>
          <Form.Item label="时间范围" name="dateRange">
            <DatePicker.RangePicker showTime presets={datePresets()} className={styles.rangePicker} />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" icon={<SearchOutlined />} onClick={search}>
                查询
              </Button>
              <Button onClick={reset}>重置</Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      <Card>
        <Table<AuditLog>
          rowKey="id"
          columns={columns}
          dataSource={data?.logs}
          loading={isFetching}
          scroll={{ x: 1500 }}
          pagination={{
            current: page,
            pageSize,
            total: data?.total ?? 0,
            showSizeChanger: true,
            showQuickJumper: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total) => `共 ${total} 条记录`
          }}
          onChange={(pagination) => {
            setPage(pagination.current ?? 1)
            setPageSize(pagination.pageSize ?? 20)
          }}
        />
      </Card>

      <AuditLogDetailModal log={selectedLog} onClose={() => setSelectedLog(null)} />
    </div>
  )
}
